import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

const KEY = 'plyez.theme';

interface ThemeApi {
  theme: Theme;
  setTheme: (t: Theme) => void;
  resolved: 'light' | 'dark';
}

const Ctx = createContext<ThemeApi | null>(null);

/**
 * Three-state theme: 'light' / 'dark' / 'system'. 'system' tracks the
 * OS preference dynamically. Persisted to localStorage; the html `dark`
 * class is toggled imperatively so Tailwind's `dark:` variants engage.
 *
 * Default is 'system' which lands on light unless the OS says
 * otherwise — matches the spec's "default light, dark first-class" rule.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system';
    return (localStorage.getItem(KEY) as Theme) || 'system';
  });
  const [resolved, setResolved] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    function apply() {
      const next = theme === 'system' ? (mq.matches ? 'dark' : 'light') : theme;
      setResolved(next);
      document.documentElement.classList.toggle('dark', next === 'dark');
      document.documentElement.style.colorScheme = next;
    }
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [theme]);

  function setTheme(t: Theme) {
    setThemeState(t);
    if (typeof window !== 'undefined') localStorage.setItem(KEY, t);
  }

  return <Ctx.Provider value={{ theme, setTheme, resolved }}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTheme() must be inside <ThemeProvider>');
  return c;
}
